import { authApi, uploadApi } from "./index";

// Helper for local storage operations
const LOCAL_STORAGE_KEY = "amalgamate_local_offerings_v3";

const getLocalOfferings = () => {
  const data = localStorage.getItem(LOCAL_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalOfferings = (offerings) => {
  localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(offerings));
};

// Fallback mock data has been removed to avoid displaying dummy offerings.


// --- API METHODS ---

export const createOffering = async (payload) => {
  try {
    const response = await authApi.post("/Offerings", payload);
    // Returns OfferingDetailDto inside the .data wrapper of standard response Envelope
    return response.data.data || response.data;
  } catch (error) {
    console.warn("Backend API POST /Offerings not reachable. Saving locally.", error);
    const offerings = getLocalOfferings();
    
    // Generate UUID format
    const newId = 'off_' + Math.random().toString(36).substr(2, 9) + '-' + Math.random().toString(36).substr(2, 4);
    const newOffering = {
      ...payload,
      offeringId: newId,
      providerId: localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6",
      createdAt: new Date().toISOString(),
      updatedAt: null
    };

    offerings.unshift(newOffering);
    saveLocalOfferings(offerings);
    return newOffering;
  }
};

export const getOfferings = async (params = {}) => {
  try {
    const response = await authApi.get("/Offerings", { params });
    const body = response.data;
    
    // Case 1: Standard double envelope: { data: { data: [...], pagination: {...} } }
    if (body && body.data && Array.isArray(body.data.data)) {
      return body.data;
    }
    
    // Case 2: Direct envelope: { data: [...], pagination: {...} }
    if (body && Array.isArray(body.data)) {
      return body;
    }
    
    // Case 3: Just the array: [...]
    if (Array.isArray(body)) {
      return { data: body, pagination: { page: 1, limit: body.length, total: body.length } };
    }
    
    // Case 4: Envelope wraps array directly: { data: [...] }
    if (body && body.data && Array.isArray(body.data)) {
      return { data: body.data, pagination: { page: 1, limit: body.data.length, total: body.data.length } };
    }
    
    return { data: [], pagination: { page: 1, limit: 20, total: 0 } };
  } catch (error) {
    console.warn("Backend API GET /Offerings not reachable. Reading local storage.", error);
    let offerings = getLocalOfferings();
    const currentUserId = localStorage.getItem("userId") || "9fa85f64-5717-4562-b3fc-2c963f66afa6";

    // Filtering logic
    if (params.providerId) {
      offerings = offerings.filter(o => o.providerId === params.providerId);
    }

    if (params.status) {
      offerings = offerings.filter(o => o.status === params.status);
    } else {
      // Public view fallback: non-owners see Active only
      if (params.providerId !== currentUserId) {
        offerings = offerings.filter(o => o.status === "active");
      }
    }

    if (params.category) {
      offerings = offerings.filter(o => o.category.toLowerCase() === params.category.toLowerCase());
    }

    if (params.subcategory) {
      offerings = offerings.filter(o => o.subcategory.toLowerCase() === params.subcategory.toLowerCase());
    }

    if (params.pricingModel) {
      offerings = offerings.filter(o => o.pricingModel === params.pricingModel);
    }

    if (params.search) {
      const searchLower = params.search.toLowerCase();
      offerings = offerings.filter(
        o => o.title.toLowerCase().includes(searchLower) || o.description?.toLowerCase().includes(searchLower)
      );
    }

    // Sort order
    if (params.sort) {
      switch (params.sort) {
        case "oldest":
          offerings.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
          break;
        case "price_asc":
          offerings.sort((a, b) => (a.price || 0) - (b.price || 0));
          break;
        case "price_desc":
          offerings.sort((a, b) => (b.price || 0) - (a.price || 0));
          break;
        case "newest":
        default:
          offerings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      }
    } else {
      offerings.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    }

    // Pagination
    const page = parseInt(params.page) || 1;
    const limit = parseInt(params.limit) || 20;
    const start = (page - 1) * limit;
    const end = page * limit;

    const dataSlice = offerings.slice(start, end).map(offering => ({
      ...offering,
      provider: {
        providerId: offering.providerId,
        name: "Adventure Club",
        avatarUrl: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80",
        bio: "Certified local outdoor guides.",
        memberSince: "2025-01-15T00:00:00Z"
      }
    }));

    return {
      data: dataSlice,
      pagination: {
        page,
        limit,
        total: offerings.length
      }
    };
  }
};

export const getOfferingById = async (id) => {
  try {
    const response = await authApi.get(`/Offerings/${id}`);
    // Envelope wraps it inside { data: OfferingDetailDto }
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API GET /Offerings/${id} not reachable. Reading locally.`, error);
    const offerings = getLocalOfferings();
    const offering = offerings.find(o => o.offeringId === id);
    if (!offering) {
      throw new Error("Offering not found");
    }

    return {
      ...offering,
      provider: {
        providerId: offering.providerId,
        name: "Adventure Club",
        avatarUrl: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=150&q=80",
        bio: "Certified local outdoor guides.",
        memberSince: "2025-01-15T00:00:00Z"
      }
    };
  }
};

export const updateOffering = async (id, payload) => {
  try {
    const response = await authApi.put(`/Offerings/${id}`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PUT /Offerings/${id} not reachable. Updating locally.`, error);
    const offerings = getLocalOfferings();
    const index = offerings.findIndex(o => o.offeringId === id);
    if (index === -1) {
      throw new Error("Offering not found");
    }

    const updated = {
      ...offerings[index],
      ...payload,
      updatedAt: new Date().toISOString()
    };

    offerings[index] = updated;
    saveLocalOfferings(offerings);
    return updated;
  }
};

export const toggleOfferingStatus = async (id, status) => {
  try {
    const response = await authApi.patch(`/Offerings/${id}/status`, { status });
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API PATCH /Offerings/${id}/status not reachable. Updating locally.`, error);
    const offerings = getLocalOfferings();
    const index = offerings.findIndex(o => o.offeringId === id);
    if (index === -1) {
      throw new Error("Offering not found");
    }

    offerings[index].status = status;
    offerings[index].updatedAt = new Date().toISOString();
    saveLocalOfferings(offerings);
    return offerings[index];
  }
};

export const deleteOffering = async (id) => {
  try {
    const response = await authApi.delete(`/Offerings/${id}`);
    if (response.data && response.data.success === false) {
      throw new Error(response.data.message || "Failed to delete offering");
    }
    return response.data;
  } catch (error) {
    if (error.response) {
      throw new Error(error.response.data?.message || "Failed to delete offering");
    }
    // Rethrow if we threw it inside the try block
    if (error.message && !error.message.includes("Network Error") && !error.message.includes("status code")) {
      throw error;
    }
    console.warn(`Backend API DELETE /Offerings/${id} not reachable. Deleting locally.`, error);
    const offerings = getLocalOfferings();
    const filtered = offerings.filter(o => o.offeringId !== id);
    saveLocalOfferings(filtered);
    return {
      success: true,
      message: "Offering deleted successfully"
    };
  }
};

// Keep file upload as a utility (can fallback to object url client-side)
export const uploadOfferingImage = async (file) => {
  const formData = new FormData();
  formData.append("file", file);
  const response = await uploadApi.post("/v1/upload/image", formData);
  return response.data;
};

// --- OFFERING PRODUCTS API (PHASE 6) ---

const PRODUCT_STORAGE_KEY = "amalgamate_local_products_v3";

const getLocalProducts = () => {
  const data = localStorage.getItem(PRODUCT_STORAGE_KEY);
  return data ? JSON.parse(data) : [];
};

const saveLocalProducts = (products) => {
  localStorage.setItem(PRODUCT_STORAGE_KEY, JSON.stringify(products));
};

export const addOfferingProduct = async (offeringId, payload) => {
  try {
    const response = await authApi.post(`/Offerings/${offeringId}/products`, payload);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API POST /Offerings/${offeringId}/products not reachable. Saving locally.`, error);
    const products = getLocalProducts();
    const newId = 'prod_' + Math.random().toString(36).substr(2, 9);
    const newProduct = {
      ...payload,
      productId: newId,
      offeringId: offeringId,
      createdAt: new Date().toISOString()
    };
    products.push(newProduct);
    saveLocalProducts(products);
    return newProduct;
  }
};

export const getOfferingProducts = async (offeringId) => {
  try {
    const response = await authApi.get(`/Offerings/${offeringId}/products`);
    return response.data.data || response.data;
  } catch (error) {
    console.warn(`Backend API GET /Offerings/${offeringId}/products not reachable. Reading local storage.`, error);
    const products = getLocalProducts();
    return products.filter(p => p.offeringId === offeringId);
  }
};

export const deleteOfferingProduct = async (offeringId, productId) => {
  try {
    const response = await authApi.delete(`/Offerings/${offeringId}/products/${productId}`);
    return response.data;
  } catch (error) {
    console.warn(`Backend API DELETE /Offerings/${offeringId}/products/${productId} not reachable. Deleting locally.`, error);
    const products = getLocalProducts();
    const filtered = products.filter(p => p.productId !== productId);
    saveLocalProducts(filtered);
    return { success: true, message: "Product deleted successfully" };
  }
};

export const exploreOfferings = async (params = {}) => {
  try {
    const response = await authApi.get("/Explore", { params });
    return response.data.data || response.data;
  } catch (error) {
    console.error("Explore API failed, falling back to getOfferings logic", error);
    return getOfferings(params);
  }
};
